import React, { FC, MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from '@microservices/wiskey-react-components'
import { GridRenderCellParams, MuiEvent } from '@microservices/wiskey-react-table-component'

import { useTooltip } from '@hooks'
import { cellRenderer, getExpandedPath } from '@helpers'
import { ACTION_CODE, ENTITY, EVENT_CODE, FORM_TYPE } from '@constants'
import { ConfiguredColumn, Position, TimeLineDTOType } from '@types'

import { useClickHandler, useHandlers } from '../../hooks'

type ObjectYCellProps = {
  params: GridRenderCellParams
  item: ConfiguredColumn
  handlers: ReturnType<typeof useHandlers>
  config: TimeLineDTOType
}

export const ObjectYCell: FC<ObjectYCellProps> = props => {
  const { params, item, handlers, config } = props

  const {
    data: { objectYActionsObject, objectYPKField },
    handlers: { handleExecuteActions },
  } = handlers

  const { onClick: onClickWithDelay, onDoubleClick } = useClickHandler({ delay: 250 })

  const { tooltipPosition, tooltipData, handleCloseTooltip, handleOpenTooltip } = useTooltip()

  const getFormProps = () => {
    return {
      title: '',
      event: FORM_TYPE.EDIT,
      viewCode: '',
      id: '',
      objectId: objectYPKField?.name ? params.row.yElement[objectYPKField?.name] : '',
      path: '',
      entityType: ENTITY.VIEW,
      isIndependentForm: true,
    }
  }

  const handleMouseOver = (event: MouseEvent<HTMLDivElement>) => {
    if (
      objectYActionsObject?.[EVENT_CODE.ON_ROW_MOUSE_OVER]?.actionCode === ACTION_CODE.OPEN_TOOLTIP
    ) {
      event.stopPropagation()
      handleOpenTooltip(event, {
        srcObj: params.row.yElement,
        action: objectYActionsObject[EVENT_CODE.ON_ROW_MOUSE_OVER],
      })
    }
  }

  const handleOnClick = (event: MuiEvent<React.MouseEvent>) => {
    const actions = config.objectY.actions

    if (actions?.some(action => action.eventCode === EVENT_CODE.ON_ROW_CLICK)) {
      onClickWithDelay(event, () => {
        event.stopPropagation()

        handleExecuteActions({
          actions,
          eventCode: EVENT_CODE.ON_ROW_CLICK,
          srcObj: {
            gridCellParams: params,
            formProps: getFormProps(),
            event,
            additionalProps: {
              addHistory: true,
            },
          },
        })
      })
    }
  }

  const handleOnDoubleClick = (event: MuiEvent<React.MouseEvent>) => {
    const actions = config.objectY.actions

    if (actions?.some(action => action.eventCode === EVENT_CODE.ON_ROW_DOUBLE_LEFT_CLICK)) {
      onDoubleClick(event, () => {
        event.stopPropagation()

        handleExecuteActions({
          actions,
          eventCode: EVENT_CODE.ON_ROW_DOUBLE_LEFT_CLICK,
          srcObj: {
            gridCellParams: params,
            formProps: getFormProps(),
            event,
            additionalProps: {
              addHistory: true,
            },
          },
        })
      })
    }
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
      onClick={handleOnClick}
      onDoubleClick={handleOnDoubleClick}
      onMouseEnter={handleMouseOver}
      onMouseOut={handleCloseTooltip}
    >
      {Boolean(tooltipPosition) &&
        (createPortal(
          <Tooltip
            lines={tooltipData?.items ?? []}
            loading={tooltipData?.loading}
            metaData={tooltipData?.metaData ?? []}
            position={tooltipPosition as Position}
            width={tooltipData?.width ?? null}
          />,
          document.body
        ) as React.ReactNode)}
      {cellRenderer(
        {
          ...item,
          code: item.pathArray?.length
            ? getExpandedPath(`${item.value}.${item.objectValue}`).join('.')
            : item.value,
        },
        {
          ...params,
          // Данные по объектуY хранятся в yElement
          row: params.row.yElement,
        },
        false
      )}
    </div>
  )
}
